import { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { Users, Shield, Crown, Lock } from 'lucide-react';
import axios from 'axios';

const API_URL = 'https://cyberguard-api-ka8f.onrender.com';

export default function AdminDashboard() {
  const { token } = useAuth();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [period, setPeriod] = useState('last_30_days');

  useEffect(() => {
    fetchAdminData();
  }, [token]);

  const fetchAdminData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [statsRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/stats`, { headers }),
        axios.get(`${API_URL}/api/admin/users`, { headers })
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
    } catch (err) {
      console.error("Erreur Admin:", err);
      if (err.response?.status === 403 || err.response?.status === 401) setForbidden(err.response?.data?.detail || true);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePlan = async (userId, newPlan) => {
    try {
      await axios.put(`${API_URL}/api/admin/users/${userId}/plan`, 
        { plan: newPlan }, 
        { headers: { Authorization: `Bearer ${token}` }}
      );
      fetchAdminData(); // Rafraîchir la liste
    } catch (err) {
      alert("Erreur lors du changement de plan");
    }
  };

  if (loading) return <DashboardLayout><div className="p-10 font-mono text-zinc-500">ACCÈS_SYSTÈME_ADMIN...</div></DashboardLayout>;
  if (forbidden) return (
    <DashboardLayout>
      <div className="p-10 font-mono text-zinc-400 flex items-center gap-3"><Lock size={16} /> {typeof forbidden === 'string' && forbidden.startsWith('Double') ? <span>La double authentification est obligatoire pour l'administration. <a href="/settings" className="text-blue-400 underline">Activez-la dans Paramètres</a>.</span> : 'Accès réservé aux administrateurs.'}</div>
    </DashboardLayout>
  );

  const funnel = stats?.funnel?.[period];
  const fmt = (v) => (v === null || v === undefined ? '—' : `${v} %`);

  return (
    <DashboardLayout>
      <div className="space-y-8 animate-in fade-in duration-500">
        <h1 className="text-2xl font-black text-white font-mono uppercase tracking-tighter border-l-4 border-red-600 pl-4">
          Terminal_Administrateur
        </h1>

        {/* STATS GLOBALES */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-zinc-900 border border-zinc-800 p-6">
            <div className="flex justify-between text-zinc-500 text-[10px] font-mono mb-2 uppercase">Utilisateurs <Users size={14}/></div>
            <div className="text-3xl font-black text-white">{stats?.total_users}</div>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 p-6">
            <div className="flex justify-between text-zinc-500 text-[10px] font-mono mb-2 uppercase">Total Audits <Shield size={14}/></div>
            <div className="text-3xl font-black text-white">{stats?.total_audits}</div>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 p-6">
            <div className="flex justify-between text-zinc-500 text-[10px] font-mono mb-2 uppercase">Plans Pro/Ent <Crown size={14}/></div>
            <div className="text-3xl font-black text-blue-500">{stats?.plans.pro + stats?.plans.enterprise}</div>
          </div>
        </div>

        {/* ENTONNOIR DE CONVERSION */}
        {funnel && (
          <div className="bg-zinc-900 border border-zinc-800 p-6 space-y-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="font-mono text-[10px] text-zinc-500 uppercase tracking-widest">Conversion : inscription → audit gratuit → abonnement</h2>
              <div className="flex gap-2">
                {[['last_30_days', '30 derniers jours'], ['all_time', 'Depuis le début']].map(([k, label]) => (
                  <button key={k} onClick={() => setPeriod(k)}
                    className={`px-3 py-1 text-[10px] font-mono uppercase border ${period === k ? 'border-blue-500 text-blue-400' : 'border-zinc-700 text-zinc-500 hover:text-white'}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { label: 'Inscrits', value: funnel.signups, rate: null },
                { label: 'Ont lancé un audit', value: funnel.activated, rate: funnel.activation_rate, rateLabel: 'des inscrits' },
                { label: 'Abonnés payants', value: funnel.paying, rate: funnel.conversion_rate, rateLabel: 'des inscrits' },
              ].map((step) => (
                <div key={step.label} className="bg-black/40 border border-zinc-800 p-4">
                  <div className="text-zinc-500 text-[10px] font-mono uppercase mb-1">{step.label}</div>
                  <div className="text-3xl font-black text-white font-mono">{step.value}</div>
                  {step.rateLabel && <div className="text-xs font-mono text-blue-400 mt-1">{fmt(step.rate)} {step.rateLabel}</div>}
                </div>
              ))}
            </div>
            <p className="text-zinc-500 text-xs font-mono">
              Parmi ceux qui ont lancé un audit, <span className="text-white">{fmt(funnel.paid_after_audit_rate)}</span> sont devenus abonnés payants.
              Comptes administrateurs exclus ; un plan attribué à la main depuis cette page compte comme payant.
            </p>
          </div>
        )}

        {/* LISTE DES UTILISATEURS */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-sm">
          <div className="p-4 border-b border-zinc-800 font-mono text-[10px] text-zinc-500 uppercase tracking-widest">
            Gestion_Base_Utilisateurs
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left font-mono text-xs">
              <thead className="bg-black/50 text-zinc-500 uppercase">
                <tr>
                  <th className="p-4">Utilisateur</th>
                  <th className="p-4">Plan Actuel</th>
                  <th className="p-4">Activité</th>
                  <th className="p-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {users.map(u => (
                  <tr key={u.user_id} className="hover:bg-white/[0.02]">
                    <td className="p-4">
                      <div className="text-white font-bold">{u.name}</div>
                      <div className="text-zinc-500 text-[10px]">{u.email}</div>
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase ${
                        u.subscription_plan === 'free' ? 'bg-zinc-800 text-zinc-400' : 'bg-blue-900/30 text-blue-400'
                      }`}>
                        {u.subscription_plan}
                      </span>
                    </td>
                    <td className="p-4 text-zinc-500">
                      {u.audit_count} scan{u.audit_count > 1 ? 's' : ''}
                    </td>
                    <td className="p-4 text-right space-x-2">
                      <select 
                        className="bg-black border border-zinc-800 text-[10px] p-1 outline-none focus:border-blue-500"
                        onChange={(e) => handleUpdatePlan(u.user_id, e.target.value)}
                        value={u.subscription_plan}
                      >
                        <option value="free">FREE</option>
                        <option value="starter">STARTER</option>
                        <option value="pro">PRO</option>
                        <option value="enterprise">ENT</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
